/**
 * /campaigns/:id/jobs/:jobId — per-job variants + render view.
 *
 * Loads variants from the DB by jobId so the URL is the source of
 * truth (closes the "variants disappear on tab switch" friction).
 * Polls the job row for status while a render is in flight; shows
 * the matched-asset thumbnails per scene and a Re-match button on
 * each variant card.
 *
 * Approve flow:
 *   variant has voiceover_text  → POST /api/marketing/voiceover
 *                                  → status: audio_generating → render_queued
 *   variant has no voiceover    → POST /api/marketing/jobs
 *                                  → status: render_queued
 *
 * Render worker (Fly machine) flips status to `rendering` → `done`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiJson } from '../../lib/api';
import { supabase } from '../../lib/supabase';
import type { JobStatusResponse, ScriptVariant, VoiceoverResponse } from '../../lib/types';
import { useCampaignContext } from '../CampaignDetail';

interface VariantRow {
  id: string;
  hook: string;
  caption: string;
  voiceover_text: string;
  storyboard: ScriptVariant['storyboard'];
  render_url: string | null;
  thumbnail_url: string | null;
  created_at: string;
  // Sub-2.5: per-variant render lifecycle. 'idle' = never rendered;
  // queued/rendering = currently in flight; done = render_url set;
  // failed = render_error set. The is_winner flag is set by the
  // promote-winner action.
  render_status: 'idle' | 'queued' | 'rendering' | 'done' | 'failed';
  render_error: string | null;
  is_winner: boolean;
}

type VariantUpdate = Partial<VariantRow> & { id: string };

const STATUS_LABELS: Record<string, string> = {
  brief: 'Brief',
  script_generating: 'Generating script…',
  script_ready: 'Variants ready',
  variant_approved: 'Variant approved',
  audio_generating: 'Generating voiceover…',
  render_queued: 'Render queued',
  rendering: 'Rendering…',
  done: 'Done',
  failed: 'Failed',
};

interface AssetSummary {
  id: string;
  type: string;
  // Provenance — used to badge scene thumbnails so the admin can
  // see at a glance which scenes were filled by the Pexels fallback
  // vs. picked from the campaign's curated library vs. generated
  // via the manual AI button. Same values as the CHECK constraint
  // in migration 109.
  source: string;
  public_url: string | null;
  description: string;
  mime_type: string | null;
}

export function JobView() {
  const { campaign, refreshJobs } = useCampaignContext();
  const { jobId } = useParams<{ jobId: string }>();
  const [variants, setVariants] = useState<VariantRow[] | null>(null);
  const [job, setJob] = useState<JobStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [voiceoverInfo, setVoiceoverInfo] = useState<{
    provider: 'elevenlabs' | 'openai';
    voice_id: string;
    model: string;
    fallback_reason: string | null;
  } | null>(null);
  const [assetMap, setAssetMap] = useState<Map<string, AssetSummary> | null>(null);

  // ── load variants (once per job) via supabase JS ────────────────
  const reloadVariants = useCallback(async (): Promise<VariantRow[] | null> => {
    if (!jobId) return null;
    const { data, error } = await supabase
      .from('marketing_video_variants')
      .select(
        'id, hook, caption, voiceover_text, storyboard, render_url, thumbnail_url, created_at, render_status, render_error, is_winner',
      )
      .eq('job_id', jobId)
      .order('created_at', { ascending: true });
    if (error) {
      setError(error.message);
      return null;
    }
    const next = (data ?? []) as VariantRow[];
    setVariants(next);
    return next;
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      const next = await reloadVariants();
      if (cancelled) return;
      if (next === null) setVariants([]);
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId, reloadVariants]);

  // ── poll variants while any render is in flight ──────────────────
  // Cheap (one Postgres scan against the partial render_status
  // index) and matches the cadence of the job-status poller. The
  // job poller alone isn't enough — render-all keeps the job at
  // 'rendering' until ALL variants settle, but individual variants
  // flip render_status / render_url asynchronously, and the grid
  // needs those refreshed to show per-variant progress.
  useEffect(() => {
    if (!jobId || !variants) return;
    const anyInFlight = variants.some(
      (v) => v.render_status === 'queued' || v.render_status === 'rendering',
    );
    if (!anyInFlight) return;
    let cancelled = false;
    const interval = window.setInterval(async () => {
      if (cancelled) return;
      const next = await reloadVariants();
      if (cancelled || !next) return;
      const stillInFlight = next.some(
        (v) => v.render_status === 'queued' || v.render_status === 'rendering',
      );
      if (!stillInFlight) window.clearInterval(interval);
    }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [jobId, variants, reloadVariants]);

  // ── load campaign asset library for thumbnails (once) ───────────
  useEffect(() => {
    let cancelled = false;
    apiJson<{ assets: AssetSummary[] }>(
      `/api/marketing/assets?campaign_id=${encodeURIComponent(campaign.id)}&limit=200`,
    )
      .then((res) => {
        if (cancelled) return;
        setAssetMap(new Map(res.assets.map((a) => [a.id, a])));
      })
      .catch(() => {
        if (cancelled) return;
        setAssetMap(new Map());
      });
    return () => {
      cancelled = true;
    };
  }, [campaign.id]);

  // ── poll job status while render in flight ──────────────────────
  const pollRef = useRef<number | null>(null);
  const stopPolling = useCallback(() => {
    if (pollRef.current != null) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;

    async function tick() {
      try {
        const next = await apiJson<JobStatusResponse>(`/api/marketing/jobs/${jobId}`);
        if (cancelled) return;
        setJob(next);
        if (next.status === 'done' || next.status === 'failed') {
          stopPolling();
          void refreshJobs();
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Status check failed');
      }
    }
    void tick();
    pollRef.current = window.setInterval(tick, 3000);
    return () => {
      cancelled = true;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  async function handleApprove(variant: VariantRow) {
    if (!jobId) return;
    setSubmitting(true);
    setError(null);
    try {
      const hasVoiceover = variant.voiceover_text.trim().length > 0;
      if (hasVoiceover) {
        const res = await apiJson<VoiceoverResponse>('/api/marketing/voiceover', {
          method: 'POST',
          body: JSON.stringify({ job_id: jobId, variant_id: variant.id }),
        });
        setVoiceoverInfo({
          provider: res.provider,
          voice_id: res.voice_id,
          model: res.model,
          fallback_reason: res.fallback_reason,
        });
      } else {
        await apiJson('/api/marketing/jobs', {
          method: 'POST',
          body: JSON.stringify({ job_id: jobId, variant_id: variant.id }),
        });
      }
      // Refresh variants so the approved one shows render_status='queued'
      // immediately rather than waiting for the next poll tick.
      await reloadVariants();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Approve failed';
      const friendly = humanizeError(message);
      window.alert(friendly);
      setError(friendly);
    } finally {
      setSubmitting(false);
    }
  }

  // Sub-2.5: render every variant in parallel for side-by-side
  // comparison. Skips the voiceover step deliberately — render-all
  // is for visual A/B comparison. Once the admin promotes a winner,
  // they can run a full Approve on that one variant to get the
  // voiceover'd final cut (which overwrites the silent render via
  // the per-variant R2 key).
  async function handleRenderAll() {
    if (!jobId) return;
    if (!window.confirm('Render all variants? This will queue ' +
      `${variants?.length ?? 0} silent renders. Voiceover + final audio render happens when you Promote a winner.`)) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiJson(`/api/marketing/jobs/${jobId}/render-all`, { method: 'POST' });
      await reloadVariants();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Render all failed';
      const friendly = humanizeError(message);
      window.alert(friendly);
      setError(friendly);
    } finally {
      setSubmitting(false);
    }
  }

  // Promote = full delivery. Marks the variant as the winner AND
  // chains into the voiceover pipeline so the winner is re-rendered
  // with audio (per-variant R2 key overwrites the silent render
  // from render-all). When the variant has no voiceover_text the
  // chain falls through to /api/marketing/jobs which queues a
  // silent re-render — same as the legacy Approve path.
  //
  // Two sequential API calls is intentional: it keeps the promote
  // and voiceover endpoints single-purpose and matches the existing
  // Approve flow's behaviour exactly (just preceded by the winner-
  // flag write). The button stays disabled across both calls so
  // the admin can't double-click into a partial state.
  async function handlePromote(variant: VariantRow) {
    if (!jobId) return;
    setSubmitting(true);
    setError(null);
    try {
      // 1. Mark winner (demotes siblings, writes audit row)
      await apiJson(`/api/marketing/variants/${variant.id}/promote`, { method: 'POST' });
      setVariants((current) =>
        current
          ? current.map((v) => ({ ...v, is_winner: v.id === variant.id }))
          : current,
      );

      // 2. Finalise: voiceover + re-render (or silent re-render if
      // the variant has no voiceover text). Mirrors handleApprove.
      const hasVoiceover = variant.voiceover_text.trim().length > 0;
      if (hasVoiceover) {
        const res = await apiJson<VoiceoverResponse>('/api/marketing/voiceover', {
          method: 'POST',
          body: JSON.stringify({ job_id: jobId, variant_id: variant.id }),
        });
        setVoiceoverInfo({
          provider: res.provider,
          voice_id: res.voice_id,
          model: res.model,
          fallback_reason: res.fallback_reason,
        });
      } else {
        await apiJson('/api/marketing/jobs', {
          method: 'POST',
          body: JSON.stringify({ job_id: jobId, variant_id: variant.id }),
        });
      }

      await reloadVariants();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Promote failed';
      const friendly = humanizeError(message);
      window.alert(friendly);
      setError(friendly);
    } finally {
      setSubmitting(false);
    }
  }

  if (!jobId) {
    return (
      <div className="alert alert-error">
        Missing job id.{' '}
        <Link to={`/campaigns/${campaign.id}/jobs`}>← Back to jobs</Link>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Link
          to={`/campaigns/${campaign.id}/jobs`}
          style={{ fontSize: 13, color: 'var(--color-text-muted)' }}
        >
          ← All jobs for this campaign
        </Link>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          {error}
        </div>
      )}

      {job && (
        <JobStatusCard
          job={job}
          voiceover={voiceoverInfo}
          selectedVariant={variants?.find((v) => v.id === job.selected_variant_id) ?? null}
          variants={variants}
          submitting={submitting}
          onRenderAll={handleRenderAll}
        />
      )}

      {variants === null && (
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <div className="spinner" />
        </div>
      )}

      {variants && variants.length === 0 && (
        <div
          className="card"
          style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)' }}
        >
          No variants for this job. The script generation may have failed — check the job status.
        </div>
      )}

      {variants && variants.length > 0 && (
        <div className="variant-grid" style={{ marginTop: 16 }}>
          {variants.map((variant) => (
            <VariantCard
              key={variant.id}
              variant={variant}
              campaignId={campaign.id}
              jobStatus={job?.status ?? null}
              assetMap={assetMap}
              disabled={submitting}
              onApprove={() => handleApprove(variant)}
              onPromote={() => handlePromote(variant)}
              onVariantUpdated={(next) =>
                setVariants((current) =>
                  current ? current.map((v) => (v.id === next.id ? { ...v, ...next } : v)) : current,
                )
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── variant card ───────────────────────────────────────────────────

function VariantCard({
  variant,
  campaignId,
  jobStatus,
  assetMap,
  disabled,
  onApprove,
  onPromote,
  onVariantUpdated,
}: {
  variant: VariantRow;
  campaignId: string;
  jobStatus: string | null;
  assetMap: Map<string, AssetSummary> | null;
  disabled: boolean;
  onApprove: () => void;
  onPromote: () => void;
  onVariantUpdated: (next: VariantUpdate) => void;
}) {
  const [rematching, setRematching] = useState(false);
  const [rematchError, setRematchError] = useState<string | null>(null);
  const [rematchSummary, setRematchSummary] = useState<string | null>(null);
  // Voiceover edit state. Kept local; we only PATCH the DB on Save
  // so admins can experiment freely without writing each keystroke.
  const [editingVoiceover, setEditingVoiceover] = useState(false);
  const [voiceoverDraft, setVoiceoverDraft] = useState(variant.voiceover_text);
  const [savingVoiceover, setSavingVoiceover] = useState(false);
  const [voiceoverSaveError, setVoiceoverSaveError] = useState<string | null>(null);
  // Preview via /api/marketing/diag-tts so admins can hear what
  // ElevenLabs is going to say with the campaign's actual voice +
  // model — before kicking off a full render.
  const [previewing, setPreviewing] = useState(false);
  const [previewAudio, setPreviewAudio] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  async function handleRematch() {
    setRematching(true);
    setRematchError(null);
    setRematchSummary(null);
    try {
      // Request top-8 candidates per scene so we have headroom to
      // de-duplicate across scenes — same algorithm as the server-
      // side auto-match (see autoMatchTopAsset for the rationale).
      const matched = await apiJson<MatchResponse>('/api/marketing/match-assets', {
        method: 'POST',
        body: JSON.stringify({ variant_id: variant.id, top_n: 8 }),
      });

      // Greedy dedup: walk scenes in order; pick the least-used
      // candidate. Ties prefer higher similarity (= earlier index in
      // the candidates array). Pool smaller than scene count → assets
      // repeat round-robin only after every option's been used.
      const picks: Record<string, string | null> = {};
      const usage = new Map<string, number>();
      let withCandidates = 0;
      let distinct = 0;
      for (const scene of matched.scenes) {
        if (scene.candidates.length === 0) {
          picks[scene.scene_id] = null;
          continue;
        }
        let best = scene.candidates[0]!;
        let bestUsage = usage.get(best.asset_id) ?? 0;
        for (const c of scene.candidates) {
          const u = usage.get(c.asset_id) ?? 0;
          if (u < bestUsage) {
            best = c;
            bestUsage = u;
          }
        }
        picks[scene.scene_id] = best.asset_id;
        if (bestUsage === 0) distinct += 1;
        usage.set(best.asset_id, bestUsage + 1);
        withCandidates += 1;
      }

      const applied = await apiJson<ApplyResponse>('/api/marketing/apply-assets', {
        method: 'POST',
        body: JSON.stringify({ variant_id: variant.id, picks }),
      });
      onVariantUpdated({ ...variant, storyboard: applied.storyboard });

      // Summary calls out the distinct-asset count so admins can see
      // whether dedup actually helped (vs the old behaviour where
      // every scene might land on the same asset).
      setRematchSummary(
        withCandidates > 0
          ? `Matched ${withCandidates} of ${matched.scenes.length} scene${matched.scenes.length === 1 ? '' : 's'}` +
              ` · ${distinct} distinct asset${distinct === 1 ? '' : 's'}.`
          : 'No candidates returned — upload assets first, or check that scene intents are non-empty.',
      );
    } catch (e) {
      setRematchError(e instanceof Error ? e.message : 'Re-match failed');
    } finally {
      setRematching(false);
    }
  }

  // After a job advances past `script_ready`, the variant is locked
  // in (TTS or render queued). Disable both buttons to prevent
  // accidentally re-running TTS on the wrong variant.
  const lockedByJob =
    jobStatus !== null &&
    jobStatus !== 'script_ready' &&
    jobStatus !== 'brief' &&
    jobStatus !== 'script_generating';

  const targetSeconds = variant.storyboard.duration_seconds;
  const voiceoverStats = analyzeVoiceover(
    editingVoiceover ? voiceoverDraft : variant.voiceover_text,
    targetSeconds,
  );

  async function saveVoiceover() {
    setSavingVoiceover(true);
    setVoiceoverSaveError(null);
    try {
      const { error } = await supabase
        .from('marketing_video_variants')
        .update({ voiceover_text: voiceoverDraft })
        .eq('id', variant.id);
      if (error) throw new Error(error.message);
      onVariantUpdated({ ...variant, voiceover_text: voiceoverDraft });
      setEditingVoiceover(false);
      setPreviewAudio(null); // stale after edit
    } catch (e) {
      setVoiceoverSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSavingVoiceover(false);
    }
  }

  async function previewVoiceover() {
    setPreviewing(true);
    setPreviewError(null);
    setPreviewAudio(null);
    try {
      const text = editingVoiceover ? voiceoverDraft : variant.voiceover_text;
      if (!text.trim()) {
        throw new Error('voiceover text is empty');
      }
      const res = await apiJson<{
        provider: string;
        voice_id: string;
        model: string;
        duration_ms: number;
        audio_base64: string;
        audio_mime: string;
      }>('/api/marketing/diag-tts', {
        method: 'POST',
        body: JSON.stringify({ provider: 'elevenlabs', text }),
      });
      setPreviewAudio(`data:${res.audio_mime};base64,${res.audio_base64}`);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  }

  // Show the rendered MP4 as the card hero whenever we have one.
  // Falls back to the animated PhonePreview while no render exists
  // yet (idle, queued, or rendering for the first time).
  const hasRender = variant.render_status === 'done' && !!variant.render_url;

  return (
    <article
      className="variant-card"
      style={
        variant.is_winner
          ? { outline: '2px solid var(--accent, #f0b400)', outlineOffset: 0 }
          : undefined
      }
    >
      {hasRender ? (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <video
          src={variant.render_url!}
          poster={variant.thumbnail_url ?? undefined}
          controls
          playsInline
          style={{
            width: '100%',
            aspectRatio: '9 / 16',
            background: '#000',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        <PhonePreview variant={variant} assetMap={assetMap} />
      )}
      <RenderStatusBanner variant={variant} />
      <div className="variant-body">
        <div className="variant-title-row">
          <h3>
            {variant.is_winner && (
              <span
                style={{
                  display: 'inline-block',
                  marginRight: 6,
                  padding: '1px 6px',
                  borderRadius: 4,
                  background: 'var(--accent, #f0b400)',
                  color: '#1a1918',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  verticalAlign: 'middle',
                }}
              >
                Winner
              </span>
            )}
            {variant.storyboard.scenes[0]?.text_overlays[0]?.text.slice(0, 32) ?? 'Variant'}
          </h3>
          <span>{variant.storyboard.duration_seconds}s</span>
        </div>
        <VoiceoverEditor
          variant={variant}
          editing={editingVoiceover}
          draft={voiceoverDraft}
          stats={voiceoverStats}
          targetSeconds={targetSeconds}
          lockedByJob={lockedByJob}
          saving={savingVoiceover}
          previewing={previewing}
          previewAudio={previewAudio}
          previewError={previewError}
          saveError={voiceoverSaveError}
          onStartEdit={() => {
            setVoiceoverDraft(variant.voiceover_text);
            setVoiceoverSaveError(null);
            setEditingVoiceover(true);
          }}
          onCancelEdit={() => {
            setEditingVoiceover(false);
            setVoiceoverSaveError(null);
          }}
          onChangeDraft={setVoiceoverDraft}
          onSave={saveVoiceover}
          onPreview={previewVoiceover}
          onResetDraft={() => setVoiceoverDraft(variant.voiceover_text)}
        />
        <MusicPicker
          variantId={variant.id}
          storyboard={variant.storyboard}
          assetMap={assetMap}
          disabled={disabled || lockedByJob}
          onUpdated={(nextStoryboard) =>
            onVariantUpdated({ ...variant, storyboard: nextStoryboard })
          }
        />
        <ol className="scene-list">
          {variant.storyboard.scenes.map((scene, idx) => {
            const start = variant.storyboard.scenes
              .slice(0, idx)
              .reduce((sum, s) => sum + s.duration_seconds, 0);
            const end = start + scene.duration_seconds;
            const asset = scene.visual.asset_id ? assetMap?.get(scene.visual.asset_id) : null;
            return (
              <li key={scene.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <SceneThumbnail asset={asset ?? null} loading={assetMap === null} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span>
                    {Math.round(start)}–{Math.round(end)}s
                  </span>{' '}
                  {scene.text_overlays.map((o) => o.text).join(' / ') || scene.visual.intent}
                </div>
                <RegenerateVisualButton
                  variantId={variant.id}
                  sceneId={scene.id}
                  intent={scene.visual.intent}
                  disabled={disabled || lockedByJob}
                  onUpdated={(nextStoryboard) =>
                    onVariantUpdated({ ...variant, storyboard: nextStoryboard })
                  }
                />
                <TemplatePicker
                  variantId={variant.id}
                  storyboard={variant.storyboard}
                  sceneId={scene.id}
                  currentTemplateId={scene.template_id ?? null}
                  sceneType={scene.type}
                  disabled={disabled || lockedByJob}
                  onUpdated={(nextStoryboard) =>
                    onVariantUpdated({ ...variant, storyboard: nextStoryboard })
                  }
                />
              </li>
            );
          })}
        </ol>
        {rematchError && (
          <div className="alert alert-error" style={{ fontSize: 11, marginTop: 8 }}>
            {rematchError}
          </div>
        )}
        {rematchSummary && !rematchError && (
          <div className="alert" style={{ fontSize: 11, marginTop: 8 }}>
            {rematchSummary}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={disabled || rematching || lockedByJob}
            onClick={handleRematch}
            title={
              lockedByJob
                ? 'Job has progressed past script_ready — re-match disabled'
                : 'Re-run the matcher and replace asset picks with the top-1 result per scene'
            }
          >
            {rematching ? 'Re-matching…' : 'Re-match assets'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ flex: 1, minWidth: 160 }}
            disabled={disabled || lockedByJob}
            onClick={onApprove}
            title={lockedByJob ? 'Job has progressed past script_ready' : undefined}
          >
            Approve · generate voiceover &amp; render
          </button>
        </div>
        {/*
          Promote-winner is the "finalize" action in the render-all
          flow. It marks the variant as the winner, demotes siblings,
          AND triggers voiceover + audio re-render so the winning
          MP4 has the final audio cut. Always visible — admins can
          pick a winner on script/preview alone, before all renders
          finish, and the chain still produces the audio version.
        */}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          style={{ marginTop: 8 }}
          disabled={disabled}
          onClick={onPromote}
          title={
            variant.is_winner
              ? 'Re-promote: re-runs the audit log entry and re-renders with voiceover'
              : 'Mark this variant as the winner, demote siblings, and re-render with voiceover'
          }
        >
          {variant.is_winner
            ? '★ Winner — re-render with voiceover'
            : '☆ Promote winner · voiceover + final render'}
        </button>
        <small style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-text-muted)' }}>
          <Link to={`/campaigns/${campaignId}/assets`}>Manage campaign assets →</Link>
        </small>
      </div>
    </article>
  );
}

// ── voiceover editor + analysis ───────────────────────────────────

interface VoiceoverStats {
  words: number;
  estimatedSeconds: number;
  status: 'short' | 'good' | 'long';
  // Words/second cadence assumed. ElevenLabs marketing pacing.
  wordsPerSecond: number;
  // Word range target derived from the video duration.
  targetMin: number;
  targetMax: number;
}

function analyzeVoiceover(text: string, targetSeconds: number): VoiceoverStats {
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const wordsPerSecond = 2.2; // matches the LLM prompt's pacing target
  const estimatedSeconds = words / wordsPerSecond;
  // Aim for ~85% of the video duration in speech, with a sensible
  // tolerance on either side.
  const targetMin = Math.round(targetSeconds * 1.6); // ~73% of duration
  const targetMax = Math.round(targetSeconds * 2.2); // ~100% of duration
  let status: VoiceoverStats['status'] = 'good';
  if (words < targetMin) status = 'short';
  else if (words > targetMax) status = 'long';
  return { words, estimatedSeconds, status, wordsPerSecond, targetMin, targetMax };
}

function VoiceoverEditor({
  variant,
  editing,
  draft,
  stats,
  targetSeconds,
  lockedByJob,
  saving,
  previewing,
  previewAudio,
  previewError,
  saveError,
  onStartEdit,
  onCancelEdit,
  onChangeDraft,
  onSave,
  onPreview,
  onResetDraft,
}: {
  variant: VariantRow;
  editing: boolean;
  draft: string;
  stats: VoiceoverStats;
  targetSeconds: number;
  lockedByJob: boolean;
  saving: boolean;
  previewing: boolean;
  previewAudio: string | null;
  previewError: string | null;
  saveError: string | null;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onChangeDraft: (next: string) => void;
  onSave: () => void;
  onPreview: () => void;
  onResetDraft: () => void;
}) {
  const statusColor =
    stats.status === 'good' ? 'limegreen' : stats.status === 'short' ? 'orange' : 'crimson';
  const statusLabel =
    stats.status === 'good'
      ? 'On target'
      : stats.status === 'short'
      ? 'Too short — voice will end before the video does'
      : 'Too long — voice will get cut off';

  return (
    <div
      style={{
        background: 'var(--color-surface-alt)',
        borderRadius: 8,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        marginTop: 8,
        marginBottom: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 8,
        }}
      >
        <strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Voiceover script
        </strong>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
          {stats.words} words · ~{stats.estimatedSeconds.toFixed(1)}s of {targetSeconds}s ·{' '}
          <span style={{ color: statusColor, fontWeight: 600 }}>{stats.status}</span>
        </span>
      </div>

      {editing ? (
        <>
          <textarea
            value={draft}
            onChange={(e) => onChangeDraft(e.target.value)}
            rows={5}
            style={{
              width: '100%',
              fontFamily: 'inherit',
              fontSize: 13,
              lineHeight: 1.4,
              padding: 8,
              borderRadius: 6,
              border: '1px solid var(--color-border, rgba(255,255,255,0.1))',
              background: 'var(--color-surface)',
              color: 'inherit',
              resize: 'vertical',
            }}
          />
          <small style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>
            {statusLabel}. Target {stats.targetMin}–{stats.targetMax} words for a {targetSeconds}s
            video at ~{stats.wordsPerSecond} words/sec ElevenLabs pacing.
          </small>
          {saveError && (
            <div className="alert alert-error" style={{ fontSize: 11 }}>
              {saveError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={saving || draft === variant.voiceover_text}
              onClick={onSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={saving}
              onClick={onCancelEdit}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={saving || draft === variant.voiceover_text}
              onClick={onResetDraft}
              title="Discard edits and revert to the saved voiceover"
            >
              Reset
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={previewing || !draft.trim()}
              onClick={onPreview}
              style={{ marginLeft: 'auto' }}
              title="Hear the draft via ElevenLabs without rendering the full video"
            >
              {previewing ? 'Generating…' : '🔊 Preview voiceover'}
            </button>
          </div>
        </>
      ) : (
        <>
          <p
            className="voiceover"
            style={{
              margin: 0,
              fontSize: 13,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}
          >
            {variant.voiceover_text}
          </p>
          <small style={{ color: 'var(--color-text-muted)', fontSize: 11 }}>{statusLabel}</small>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={onStartEdit}
              disabled={lockedByJob}
              title={
                lockedByJob
                  ? 'Job has progressed past script_ready — voiceover is locked in'
                  : 'Edit the voiceover script before approving'
              }
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={previewing || !variant.voiceover_text.trim()}
              onClick={onPreview}
              title="Hear the current voiceover via ElevenLabs without rendering"
            >
              {previewing ? 'Generating…' : '🔊 Preview voiceover'}
            </button>
          </div>
        </>
      )}

      {previewError && (
        <div className="alert alert-error" style={{ fontSize: 11 }}>
          {previewError}
        </div>
      )}
      {previewAudio && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio src={previewAudio} controls style={{ width: '100%' }} />
      )}
    </div>
  );
}

// ── phone preview ──────────────────────────────────────────────────

/**
 * Animated preview of how the variant will render. Auto-cycles through
 * scenes (one every ~1.6s, matching the bottom-of-funnel pacing of a
 * Reels-style ad) and composes each scene's matched asset behind the
 * text overlay — same shape as the renderer's TextCardsScene.
 * The dashboard preview doesn't mirror the v2 templates (testimonial,
 * product_demo, lifestyle) — admins rely on the rendered MP4 for an
 * accurate look once a variant has been queued.
 *
 * Hover or click to pause. Resumes on mouse leave or another click.
 * Stays a static first-scene preview until the asset library finishes
 * loading, so admins aren't staring at a flickering "loading" state.
 */
function PhonePreview({
  variant,
  assetMap,
}: {
  variant: VariantRow;
  assetMap: Map<string, AssetSummary> | null;
}) {
  const scenes = variant.storyboard.scenes;
  const [activeIdx, setActiveIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || scenes.length <= 1) return;
    const interval = window.setInterval(() => {
      setActiveIdx((i) => (i + 1) % scenes.length);
    }, 1600);
    return () => window.clearInterval(interval);
  }, [paused, scenes.length]);

  const scene = scenes[activeIdx];
  const asset = scene?.visual.asset_id ? assetMap?.get(scene.visual.asset_id) : null;
  const hasAsset = !!asset?.public_url;
  const primaryColor = variant.storyboard.brand.primary_color;
  const textColor = hasAsset ? '#FFFFFF' : variant.storyboard.brand.text_color;
  const accentColor = scene?.type === 'cta' ? primaryColor : 'transparent';

  // Pick the largest overlay text for display. Falls back to the
  // variant hook + caption if the scene has no overlays for some
  // reason (shouldn't happen post-LLM-gen).
  const headline = scene?.text_overlays[0]?.text ?? variant.hook;
  const subline = scene?.text_overlays[1]?.text ?? '';

  return (
    <div
      className="phone-preview"
      style={{
        background: hasAsset ? '#000' : `linear-gradient(160deg, ${primaryColor}, #1a1918 70%)`,
        position: 'relative',
        cursor: 'pointer',
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onClick={() => setPaused((p) => !p)}
      title={paused ? 'Click to play preview' : 'Click to pause preview'}
    >
      {hasAsset && (
        <>
          {asset.mime_type?.startsWith('video/') ? (
            <video
              key={asset.id}
              src={asset.public_url!}
              autoPlay
              muted
              loop
              playsInline
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <img
              src={asset.public_url!}
              alt={asset.description || asset.id}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}
          {/* dark gradient overlay — mirrors the renderer's */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.25) 35%, rgba(0,0,0,0.55) 100%)',
            }}
          />
        </>
      )}

      <div
        className="safe-zone"
        style={{
          position: 'relative',
          background: hasAsset ? 'transparent' : 'rgba(0, 0, 0, 0.32)',
          border: hasAsset ? 'none' : '1px solid rgba(255, 255, 255, 0.16)',
          color: textColor,
          textShadow: hasAsset
            ? '0 4px 24px rgba(0,0,0,0.55), 0 1px 2px rgba(0,0,0,0.4)'
            : 'none',
        }}
      >
        <span>
          {variant.storyboard.platform} · scene {activeIdx + 1}/{scenes.length}
        </span>
        {accentColor !== 'transparent' && (
          <div
            style={{
              width: 48,
              height: 4,
              borderRadius: 2,
              background: accentColor,
              alignSelf: 'center',
            }}
          />
        )}
        <strong
          style={{
            color: scene?.type === 'cta' && !hasAsset ? primaryColor : textColor,
          }}
        >
          {headline}
        </strong>
        {subline && <p>{subline}</p>}
      </div>

      {/* scene-cursor dots */}
      <div
        style={{
          position: 'absolute',
          bottom: 8,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          gap: 4,
          zIndex: 1,
        }}
      >
        {scenes.map((_, i) => (
          <span
            key={i}
            style={{
              width: 16,
              height: 3,
              borderRadius: 2,
              background:
                i === activeIdx ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.3)',
              transition: 'background 200ms',
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── job status card ────────────────────────────────────────────────

// Per-variant render status badge shown between the preview and the
// card body. Idle (never rendered) is silent so existing cards in
// `script_ready` look identical to before; the banner only appears
// once the variant has been queued at least once.
function RenderStatusBanner({ variant }: { variant: VariantRow }) {
  if (variant.render_status === 'idle') return null;
  const color =
    variant.render_status === 'done'
      ? 'limegreen'
      : variant.render_status === 'failed'
      ? 'crimson'
      : 'var(--color-text-muted)';
  const label =
    variant.render_status === 'queued'
      ? 'Queued for render…'
      : variant.render_status === 'rendering'
      ? 'Rendering…'
      : variant.render_status === 'done'
      ? 'Render complete'
      : 'Render failed';
  return (
    <div
      style={{
        padding: '6px 12px',
        fontSize: 11,
        background: 'var(--color-surface-alt)',
        borderTop: '1px solid var(--color-border, rgba(255,255,255,0.08))',
        borderBottom: '1px solid var(--color-border, rgba(255,255,255,0.08))',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: 6,
          background: color,
          display: 'inline-block',
        }}
      />
      <strong style={{ color }}>{label}</strong>
      {variant.render_error && (
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 10,
            color: 'crimson',
            maxWidth: '60%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={variant.render_error}
        >
          {variant.render_error}
        </span>
      )}
    </div>
  );
}

function JobStatusCard({
  job,
  voiceover,
  selectedVariant,
  variants,
  submitting,
  onRenderAll,
}: {
  job: JobStatusResponse;
  voiceover:
    | { provider: 'elevenlabs' | 'openai'; voice_id: string; model: string; fallback_reason: string | null }
    | null;
  selectedVariant: VariantRow | null;
  variants: VariantRow[] | null;
  submitting: boolean;
  onRenderAll: () => void;
}) {
  const renderUrl = job.render_url ?? selectedVariant?.render_url ?? null;
  const thumbnailUrl = job.thumbnail_url ?? selectedVariant?.thumbnail_url ?? null;

  // "Render all" is offered when there's nothing currently rendering
  // and at least one variant exists. The endpoint allows re-renders
  // after done/failed, so we keep the button visible in those states
  // too (text adjusts to "Re-render all"). selected_variant_id ≠ null
  // means single-approve has already locked a variant in — render-all
  // would clear that, which is fine but worth showing in the label.
  const renderInFlight =
    job.status === 'render_queued' ||
    job.status === 'rendering' ||
    job.status === 'audio_generating' ||
    job.status === 'variant_approved' ||
    job.status === 'script_generating';
  const hasAnyDone = (variants ?? []).some((v) => v.render_status === 'done');
  const renderAllLabel = hasAnyDone ? 'Re-render all variants' : 'Render all variants (silent)';

  return (
    <div
      className="card"
      style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}
    >
      <div className="step-row">
        <strong>Status</strong>
        <span>{STATUS_LABELS[job.status] ?? job.status}</span>
      </div>
      {variants && variants.length > 0 && (
        <div
          className="step-row"
          style={{ fontSize: 12, color: 'var(--color-text-muted)' }}
        >
          <span>Variants</span>
          <span>
            {variants.filter((v) => v.render_status === 'done').length} done ·{' '}
            {variants.filter((v) => v.render_status === 'rendering' || v.render_status === 'queued').length} in flight ·{' '}
            {variants.filter((v) => v.render_status === 'failed').length} failed
            {variants.some((v) => v.is_winner) && ' · 1 winner'}
          </span>
        </div>
      )}
      {variants && variants.length > 0 && !renderInFlight && (
        <button
          type="button"
          className="btn btn-secondary"
          disabled={submitting}
          onClick={onRenderAll}
          title="Queue every variant for rendering so you can compare them side-by-side. No voiceover — run Approve on the winner for the full audio cut."
        >
          {renderAllLabel}
        </button>
      )}
      {voiceover && (
        <div
          className={voiceover.provider === 'elevenlabs' ? 'alert' : 'alert alert-error'}
          style={{ fontSize: 13 }}
        >
          <strong>{voiceover.provider}</strong> · {voiceover.voice_id} · {voiceover.model}
          {voiceover.fallback_reason && (
            <div style={{ marginTop: 6 }}>
              <strong>Why ElevenLabs was skipped:</strong> {voiceover.fallback_reason}
            </div>
          )}
        </div>
      )}
      {job.error_message && <div className="alert alert-error">{job.error_message}</div>}
      {job.status === 'done' && renderUrl && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <video
            src={renderUrl}
            controls
            playsInline
            style={{
              width: '100%',
              maxHeight: 480,
              background: 'black',
              borderRadius: 12,
            }}
            poster={thumbnailUrl ?? undefined}
          />
          <a href={renderUrl} target="_blank" rel="noreferrer" className="btn btn-secondary">
            Open MP4 in new tab
          </a>
          {thumbnailUrl && (
            <a
              href={thumbnailUrl}
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost btn-sm"
            >
              Thumbnail
            </a>
          )}
        </div>
      )}
      {/*
        Only spin while the pipeline is actively working — not while
        we're just waiting on the admin to click Approve. script_ready
        and script_generating are pre-approval; the rest are
        post-approval and represent actual server-side work.
      */}
      {isProcessing(job.status) && <div className="spinner" />}
    </div>
  );
}

function isProcessing(status: string): boolean {
  return (
    status === 'variant_approved' ||
    status === 'audio_generating' ||
    status === 'render_queued' ||
    status === 'rendering'
  );
}

// ── shared helpers ─────────────────────────────────────────────────

interface MatchResponse {
  variant_id: string;
  campaign_id: string;
  scenes: {
    scene_id: string;
    intent: string;
    current_asset_id: string | null;
    candidates: { asset_id: string }[];
  }[];
}

interface ApplyResponse {
  variant_id: string;
  storyboard: ScriptVariant['storyboard'];
}

// ── template picker ───────────────────────────────────────────────

/**
 * Canonical template ids — must mirror the registry in
 * `marketing-renderer/src/remotion/templates/registry.ts`.
 * The SPA can't import from the renderer package (separate
 * tsconfig + no path alias), so the list is duplicated here. If you
 * add a template, update both files.
 */
const TEMPLATE_OPTIONS: { id: string; label: string; hint: string }[] = [
  { id: 'text_cards', label: 'Text cards', hint: 'Centered text over brand colour or asset' },
  { id: 'hook', label: 'Hook', hint: 'Bigger type + quick settle motion (default for scene_1)' },
  { id: 'cta', label: 'CTA', hint: 'Accent bar + brand-primary text (default for scene_5)' },
  { id: 'product_demo', label: 'Product demo', hint: 'Letterbox-fit screenshot + bottom caption' },
  { id: 'testimonial', label: 'Testimonial', hint: 'Quote + attribution, optional headshot' },
  { id: 'lifestyle', label: 'Lifestyle', hint: 'Magazine layout — asset top, branded text panel bottom' },
];

/**
 * Default-by-type mapping — mirrors TYPE_TO_DEFAULT_TEMPLATE in the
 * renderer's registry. Used to show the admin what they're overriding
 * when template_id is null ("Auto · hook" rather than just "Auto").
 */
const DEFAULT_TEMPLATE_BY_TYPE: Record<string, string> = {
  hook: 'hook',
  scene: 'text_cards',
  cta: 'cta',
  testimonial: 'testimonial',
  product_demo: 'product_demo',
  proof_card: 'testimonial',
  lifestyle: 'lifestyle',
};

function TemplatePicker({
  variantId,
  storyboard,
  sceneId,
  currentTemplateId,
  sceneType,
  disabled,
  onUpdated,
}: {
  variantId: string;
  storyboard: ScriptVariant['storyboard'];
  sceneId: string;
  currentTemplateId: string | null;
  sceneType: string;
  disabled: boolean;
  onUpdated: (next: ScriptVariant['storyboard']) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const defaultId = DEFAULT_TEMPLATE_BY_TYPE[sceneType] ?? 'text_cards';

  // Value shown in the <select>. Empty string = "Auto" (null).
  const value = currentTemplateId ?? '';

  async function handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const next = e.target.value === '' ? null : e.target.value;
    setSaving(true);
    setError(null);
    // Optimistic local update — mirror the change immediately so the
    // <select> renders the new value without waiting for the PATCH.
    const patched: ScriptVariant['storyboard'] = {
      ...storyboard,
      scenes: storyboard.scenes.map((s) =>
        s.id === sceneId ? { ...s, template_id: next } : s,
      ),
    };
    try {
      const { error: pErr } = await supabase
        .from('marketing_video_variants')
        .update({ storyboard: patched })
        .eq('id', variantId);
      if (pErr) throw new Error(pErr.message);
      onUpdated(patched);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
      <select
        value={value}
        onChange={handleChange}
        disabled={disabled || saving}
        title={
          currentTemplateId
            ? `Override: render this scene with the ${currentTemplateId} template`
            : `Auto: registry picks ${defaultId} based on scene.type=${sceneType}`
        }
        style={{
          fontSize: 11,
          padding: '2px 6px',
          background: 'var(--color-surface)',
          color: 'inherit',
          border: '1px solid var(--color-border, rgba(255,255,255,0.12))',
          borderRadius: 4,
          maxWidth: 140,
        }}
      >
        <option value="">Auto · {defaultId}</option>
        {TEMPLATE_OPTIONS.map((opt) => (
          <option key={opt.id} value={opt.id} title={opt.hint}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && (
        <span style={{ fontSize: 10, color: 'crimson' }} title={error}>
          save failed
        </span>
      )}
    </div>
  );
}

// ── music picker ──────────────────────────────────────────────────

/**
 * Per-variant background music override. The LLM auto-picks a music
 * asset at script-gen time based on the variant's `music_tone`
 * (matcher RPC, type='music' filter). Admins use this dropdown to
 * change that pick — useful when the campaign's library is small and
 * the LLM grabbed a track that doesn't fit, or to A/B different music
 * beds against the same script.
 *
 * Reads available music from the campaign-wide asset map (already
 * loaded by JobView) — no separate fetch. Globals (`campaign_id IS
 * NULL`) are included by the matcher's RLS and naturally surface here
 * because the assets endpoint includes them.
 *
 * "None" clears the selection → renderer skips the music track
 * entirely (renders with voiceover-only, same as v1 storyboards).
 */
function MusicPicker({
  variantId,
  storyboard,
  assetMap,
  disabled,
  onUpdated,
}: {
  variantId: string;
  storyboard: ScriptVariant['storyboard'];
  assetMap: Map<string, AssetSummary> | null;
  disabled: boolean;
  onUpdated: (next: ScriptVariant['storyboard']) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentId = storyboard.audio.background_music_id;

  // Filter the already-loaded asset map down to music tracks. Sorted
  // alphabetically by filename → description fallback for a stable,
  // predictable picker order.
  const musicAssets =
    assetMap === null
      ? null
      : Array.from(assetMap.values())
          .filter((a) => a.type === 'music')
          .sort((a, b) => {
            const aLabel = a.description || a.id;
            const bLabel = b.description || b.id;
            return aLabel.localeCompare(bLabel);
          });

  async function handleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const next = e.target.value === '' ? null : e.target.value;
    setSaving(true);
    setError(null);
    try {
      const res = await apiJson<{ storyboard: ScriptVariant['storyboard'] }>(
        '/api/marketing/apply-music',
        {
          method: 'POST',
          body: JSON.stringify({ variant_id: variantId, music_asset_id: next }),
        },
      );
      onUpdated(res.storyboard);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const currentAsset = currentId ? assetMap?.get(currentId) ?? null : null;
  const currentLabel = currentAsset
    ? currentAsset.description || currentAsset.id.slice(0, 8)
    : currentId
    ? `${currentId.slice(0, 8)}… (not in library)`
    : 'None — no music';

  return (
    <div
      style={{
        background: 'var(--color-surface-alt)',
        borderRadius: 8,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        marginBottom: 8,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Background music
        </strong>
        <span
          style={{ fontSize: 11, color: 'var(--color-text-muted)', maxWidth: '60%', textAlign: 'right' }}
          title={currentId ?? ''}
        >
          {currentLabel}
        </span>
      </div>
      <select
        value={currentId ?? ''}
        onChange={handleChange}
        disabled={disabled || saving || musicAssets === null}
        title="Override the LLM's music pick. None = no music track."
        style={{
          fontSize: 12,
          padding: '4px 6px',
          background: 'var(--color-surface)',
          color: 'inherit',
          border: '1px solid var(--color-border, rgba(255,255,255,0.12))',
          borderRadius: 4,
        }}
      >
        <option value="">None — no music</option>
        {musicAssets === null && <option disabled>Loading…</option>}
        {musicAssets?.length === 0 && (
          <option disabled>No music in campaign library — upload one in Assets</option>
        )}
        {musicAssets?.map((a) => (
          <option key={a.id} value={a.id}>
            {a.description || a.id.slice(0, 8)}
          </option>
        ))}
      </select>
      {currentAsset?.public_url && (
        // eslint-disable-next-line jsx-a11y/media-has-caption
        <audio src={currentAsset.public_url} controls preload="none" style={{ width: '100%', height: 28 }} />
      )}
      {error && (
        <small style={{ fontSize: 10, color: 'crimson' }} title={error}>
          {error}
        </small>
      )}
    </div>
  );
}

// Source → corner dot colour for the scene thumbnail. The dot lets
// the admin glance down the scene list and tell at-a-glance which
// scenes pulled from the campaign library (no dot) vs. were filled
// by the Pexels fallback (blue) vs. AI-generated (orange).
const SCENE_SOURCE_DOT: Record<string, string> = {
  pexels: '#60a5fa',
  generated: '#e8432d',
  // 'upload' and 'tripcode_app' = curated assets — no dot needed,
  // they're the expected default.
};

function SceneThumbnail({
  asset,
  loading,
}: {
  asset: AssetSummary | null;
  loading: boolean;
}) {
  const size = 36;
  const baseStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: 6,
    background: 'var(--color-surface-alt)',
    flexShrink: 0,
    overflow: 'hidden',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 9,
    color: 'var(--color-text-muted)',
    position: 'relative',
  };
  const dotColor = asset ? SCENE_SOURCE_DOT[asset.source] : undefined;
  // Position-absolute the corner dot relative to a wrapper so it
  // overlays whichever media type renders below.
  const sourceDot = dotColor ? (
    <span
      title={`Source: ${asset?.source ?? ''}`}
      style={{
        position: 'absolute',
        top: 2,
        right: 2,
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: dotColor,
        border: '1px solid var(--color-surface)',
        pointerEvents: 'none',
      }}
    />
  ) : null;

  if (loading) return <span style={baseStyle}>…</span>;
  if (!asset?.public_url) return <span style={baseStyle}>—</span>;
  const mime = asset.mime_type ?? '';
  if (mime.startsWith('image/')) {
    return (
      <span style={baseStyle}>
        <img
          src={asset.public_url}
          alt={asset.description || asset.id}
          title={asset.description}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {sourceDot}
      </span>
    );
  }
  if (mime.startsWith('video/')) {
    return (
      <span style={baseStyle}>
        <video
          src={asset.public_url}
          muted
          playsInline
          preload="metadata"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {sourceDot}
      </span>
    );
  }
  return (
    <span style={baseStyle}>
      {asset.type.slice(0, 3).toUpperCase()}
      {sourceDot}
    </span>
  );
}

// ── per-scene AI image-gen button (sub-2.7) ───────────────────────

/**
 * "Generate AI image" affordance shown on every scene row. The
 * click triggers OpenAI's gpt-image-1 via
 * /api/marketing/regenerate-scene-visual; on success the storyboard
 * is patched with the new asset id and `onUpdated` fires so the
 * thumbnail refreshes inline.
 *
 * No upfront enablement flag — the cost gate is the click itself.
 * Confirms first so a stray tap doesn't quietly spend $0.04, and
 * surfaces errors inline so the admin can retry without losing the
 * variant card state.
 */
function RegenerateVisualButton({
  variantId,
  sceneId,
  intent,
  disabled,
  onUpdated,
}: {
  variantId: string;
  sceneId: string;
  intent: string;
  disabled: boolean;
  onUpdated: (next: ScriptVariant['storyboard']) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    if (busy) return;
    if (!intent.trim()) {
      setError('Scene has no intent — fill in the storyboard first');
      return;
    }
    // Inline confirm rather than a modal — single line, costs the
    // admin one extra click before each $0.04 spend, no extra UI
    // chrome on the variant card.
    const ok = window.confirm(
      `Generate a new AI image for this scene?\n\nPrompt: "${intent.slice(0, 140)}${intent.length > 140 ? '…' : ''}"\n\nCost: ~$0.04 · runtime ~15s.`,
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiJson<{ storyboard: ScriptVariant['storyboard'] }>(
        '/api/marketing/regenerate-scene-visual',
        {
          method: 'POST',
          body: JSON.stringify({ variant_id: variantId, scene_id: sceneId }),
        },
      );
      onUpdated(res.storyboard);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Image gen failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      className="btn btn-ghost btn-sm"
      disabled={disabled || busy}
      onClick={run}
      title={
        error ?? 'Generate a new AI image for this scene (~$0.04, gpt-image-1).'
      }
      style={{ fontSize: 11, padding: '4px 8px', flexShrink: 0 }}
    >
      {busy ? 'Generating…' : 'AI'}
    </button>
  );
}

function humanizeError(raw: string): string {
  const m = /^\d{3} [^:]+:\s*(.*)$/s.exec(raw);
  const body = (m?.[1] ?? raw).trim();
  if (body.startsWith('voiceover_failed:')) {
    return body.slice('voiceover_failed:'.length).trim();
  }
  return body || raw;
}
